import React from 'react';
import {
	Box,
	Flex,
	Text,
	HStack,
	IconButton,
	Button,
	Image,
	Divider,
	Tooltip,
} from '@chakra-ui/react';
import { HiOutlineMenu } from 'react-icons/hi';
import { IoMdAddCircleOutline } from 'react-icons/io';
import UserDetails from '../UserProfile/UserProfileDropdown';
import NotificationSystem from './NotificationSystem';
import FollowUpHeaderAlert from './FollowUpHeaderAlert';
import EmailPackHeaderAction from './EmailPackHeaderAction';
import ClientPortalActivityHeader from './ClientPortalActivityHeader';
import { useCompanySettings } from '../../hooks/useCompany';
import defaultSizes from '../../utils/defaultSizes';
import LogoImage from '../../assets/logoHorizontalAligned.png';

const PageTitleBlock = ({
	pageTitle,
	pageDescription,
	headerText,
	compact = false,
}) => (
	<Box minW={0} flex='1'>
		<Text
			as='h1'
			fontSize={compact ? 'sm' : { base: 'md', lg: 'lg' }}
			fontWeight='semibold'
			color={headerText}
			lineHeight='short'
			noOfLines={1}
			mb={pageDescription ? 0.5 : 0}>
			{pageTitle}
		</Text>
		{pageDescription && (
			<Text fontSize={compact ? 'xs' : 'sm'} color='gray.500' noOfLines={1} mb={0}>
				{pageDescription}
			</Text>
		)}
	</Box>
);

const TopLayoutHeader = ({
	isMobile,
	onMobileMenuOpen,
	pageTitle,
	pageDescription,
	showModal,
	companyId,
	employeeId,
	topHeaderBg,
	headerText,
}) => {
	const { data: companySettings } = useCompanySettings(companyId);
	const logoSrc = companySettings?.data?.settings?.branding?.logo || LogoImage;

	const utilityActions = (
		<HStack
			spacing={0.5}
			bg='gray.50'
			border='1px solid'
			borderColor='gray.100'
			rounded='xl'
			p={0.5}
			flexShrink={0}>
			<UserDetails />
			<Divider orientation='vertical' h='28px' borderColor='gray.200' mx={0.5} />
			<NotificationSystem
				companyId={companyId}
				employeeId={employeeId}
				size='sm'
			/>
			<ClientPortalActivityHeader companyId={companyId} size='sm' />
			<FollowUpHeaderAlert size='sm' />
			<EmailPackHeaderAction size='sm' />
		</HStack>
	);

	const primaryActions = (
		<HStack spacing={2} flexShrink={0}>
			<Button
				variant='outline'
				leftIcon={<IoMdAddCircleOutline />}
				onClick={showModal}
				size='sm'
				colorScheme='green'
				rounded='lg'
				fontWeight='semibold'
				boxShadow='sm'
				_hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
				display={{ base: 'none', sm: 'inline-flex' }}>
				Todo
			</Button>
			<Tooltip label='Quick Todo' hasArrow placement='bottom'>
				<IconButton
					aria-label='Quick Todo'
					icon={<IoMdAddCircleOutline />}
					onClick={showModal}
					size='sm'
					colorScheme='teal'
					rounded='lg'
					display={{ base: 'inline-flex', sm: 'none' }}
				/>
			</Tooltip>
		</HStack>
	);

	return (
		<Box
			as='header'
			position='sticky'
			top={0}
			w='100%'
			minH={defaultSizes.headerHeight}
			bg={topHeaderBg}
			borderBottom='1px solid'
			borderColor='gray.100'
			backdropFilter='blur(10px)'
			px={{ base: 3, md: 5 }}
			zIndex={110}
			boxShadow='sm'>
			{isMobile ? (
				<Flex
					direction='column'
					justify='center'
					minH={defaultSizes.headerHeight}
					py={2}
					gap={2}>
					<Flex align='center' justify='space-between' w='100%' gap={2}>
						<Image
							src={logoSrc}
							alt='Company logo'
							h='32px'
							maxW='120px'
							objectFit='contain'
							flexShrink={0}
						/>
						<HStack spacing={1.5} flexShrink={0}>
							{utilityActions}
							{primaryActions}
							<IconButton
								aria-label='Open navigation menu'
								icon={<HiOutlineMenu />}
								onClick={onMobileMenuOpen}
								variant='ghost'
								size='sm'
								rounded='lg'
								colorScheme='teal'
							/>
						</HStack>
					</Flex>
					{(pageTitle || pageDescription) && (
						<PageTitleBlock
							pageTitle={pageTitle}
							pageDescription={pageDescription}
							headerText={headerText}
							compact
						/>
					)}
				</Flex>
			) : (
				<Flex
					align='center'
					justify='space-between'
					minH={defaultSizes.headerHeight}
					gap={4}>
					<PageTitleBlock
						pageTitle={pageTitle}
						pageDescription={pageDescription}
						headerText={headerText}
					/>
					<HStack spacing={3} flexShrink={0}>
						{utilityActions}
						{primaryActions}
					</HStack>
				</Flex>
			)}
		</Box>
	);
};

export default TopLayoutHeader;
